import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import { storageService } from "@/lib/api/services/storage.service";
import { freelancerService } from "@/lib/api/services/freelancer.service";
import { clientService } from "@/lib/api/services/client.service";
import type { Database } from "@/lib/supabase/supabase-types";

type ProfileType = 'freelancer' | 'client';

type FreelancerProfile = Database["public"]["Tables"]["freelancer_profiles"]["Row"];
type ClientProfile = Database["public"]["Tables"]["client_profiles"]["Row"];

// Extended type that includes enabled status for freelancers
type FreelancerProfileWithEnabled = FreelancerProfile & {
  is_enabled: boolean;
  enabled_at: string | null;
};

interface UseProfilePictureOptions {
  profileType: ProfileType;
  profileId?: string | null;
}

export function useProfilePictureGeneric({ profileType, profileId }: UseProfilePictureOptions) {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const getQueryKey = () => {
    return profileType === 'freelancer' 
      ? ["freelancer-profile", profileId]
      : ["client-profile", profileId];
  };


  const uploadProfilePicture = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) throw new Error("No user ID");

      // Upload file to backend storage API
      const publicUrl = await storageService.uploadFile('profile-pictures', file);

      // If profile exists, update it. Otherwise, just return the URL for later use during profile creation
      if (profileId) {
        if (profileType === 'freelancer') {
          const freelancerData = await freelancerService.updateProfile({ profile_picture_url: publicUrl });

          // Preserve existing enabled status from cache (to avoid falsely marking profile as disabled)
          const previousProfile = profileId
            ? queryClient.getQueryData<FreelancerProfileWithEnabled>(["freelancer-profile", profileId])
            : null;

          const profileWithEnabled: FreelancerProfileWithEnabled = {
            ...freelancerData,
            is_enabled:
              (freelancerData as any).is_enabled ??
              previousProfile?.is_enabled ??
              true,
            enabled_at:
              (freelancerData as any).enabled_at ??
              previousProfile?.enabled_at ??
              null,
          };

          return { 
            profileData: profileWithEnabled,
            publicUrl 
          };
        } else {
          const clientData = await clientService.updateProfile({ profile_picture_url: publicUrl });

          return { 
            profileData: clientData,
            publicUrl 
          };
        }
      } else {
        // No profile exists yet - just return the URL for later use
        return { 
          profileData: null,
          publicUrl 
        };
      }
    },
    onMutate: async (file: File) => {
      const queryKey = getQueryKey();
      
      // Cancel any outgoing refetches
      if (profileId) {
        await queryClient.cancelQueries({ queryKey });
      }

      // Snapshot the previous profile
      const previousProfile = profileId 
        ? queryClient.getQueryData<FreelancerProfileWithEnabled | ClientProfile>(queryKey)
        : null;

      // Create optimistic preview URL for the uploaded file
      const optimisticUrl = URL.createObjectURL(file);

      // Optimistically update to show the new image
      if (previousProfile && profileId) {
        const optimisticData = {
          ...previousProfile,
          profile_picture_url: optimisticUrl,
        };
        queryClient.setQueryData(queryKey, optimisticData);
      }

      return { previousProfile, optimisticUrl };
    },
    onError: (err, _file, context) => {
      const queryKey = getQueryKey();
      
      // If the mutation fails, roll back the profile cache
      if (context?.previousProfile && profileId) {
        queryClient.setQueryData(queryKey, context.previousProfile);
      }
      // Clean up the optimistic URL
      if (context?.optimisticUrl) {
        URL.revokeObjectURL(context.optimisticUrl);
      }
      console.error('Profile picture upload failed:', err);
      toast.error('Failed to upload profile picture. Please try again.');
    },
    onSuccess: (result, _file, context) => {
      const queryKey = getQueryKey();

      // Only update cache if profile exists and was updated
      if (profileId && result.profileData) {
        // If we have an optimistic URL, we need to preload the new image before switching
        if (context?.optimisticUrl && result.publicUrl) {
          const img = new Image();
          img.onload = () => {
            // New image is ready, now update cache and cleanup
            queryClient.setQueryData(queryKey, result.profileData);
            // Also invalidate related lists
            if (profileType === 'freelancer') {
              queryClient.invalidateQueries({ queryKey: ["freelancer-profiles"] });
            }
            URL.revokeObjectURL(context.optimisticUrl);
          };
          img.onerror = () => {
            // If new image fails to load, still update but keep optimistic version a bit longer
            queryClient.setQueryData(queryKey, result.profileData);
            if (profileType === 'freelancer') {
              queryClient.invalidateQueries({ queryKey: ["freelancer-profiles"] });
            }
            setTimeout(() => URL.revokeObjectURL(context.optimisticUrl), 500);
          };
          img.src = result.publicUrl;
        } else {
          // No optimistic URL, just update normally
          queryClient.setQueryData(queryKey, result.profileData);
          if (profileType === 'freelancer') {
            queryClient.invalidateQueries({ queryKey: ["freelancer-profiles"] });
          }
        }
      } else if (context?.optimisticUrl) {
        // No profile exists yet, but we had an optimistic URL, clean it up
        URL.revokeObjectURL(context.optimisticUrl);
      }
      
      toast.success('Profile picture uploaded successfully!');
    },
  });

  const deleteProfilePicture = useMutation({
    mutationFn: async (pictureUrl: string) => {
      if (!user?.id) throw new Error("No user ID");

      // Extract file path from URL
      const urlParts = pictureUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      if (!fileName) throw new Error("Invalid picture URL");

      // Construct the full path (user.id/fileName)
      const filePath = `${user.id}/${fileName}`;

      // Delete file from backend storage API
      await storageService.deleteFile('profile-pictures', filePath);

      // If profile exists, update it. Otherwise, just return success (for onboarding)
      if (profileId) {
        if (profileType === 'freelancer') {
          const freelancerData = await freelancerService.updateProfile({ profile_picture_url: null });

          // Preserve existing enabled status from cache (to avoid falsely marking profile as disabled)
          const previousProfile = profileId
            ? queryClient.getQueryData<FreelancerProfileWithEnabled>(["freelancer-profile", profileId])
            : null;

          const profileWithEnabled: FreelancerProfileWithEnabled = {
            ...freelancerData,
            is_enabled:
              (freelancerData as any).is_enabled ??
              previousProfile?.is_enabled ??
              true,
            enabled_at:
              (freelancerData as any).enabled_at ??
              previousProfile?.enabled_at ??
              null,
          };

          return { 
            profileData: profileWithEnabled
          };
        } else {
          const clientData = await clientService.updateProfile({ profile_picture_url: null });

          return { 
            profileData: clientData
          };
        }
      } else {
        // No profile exists yet (onboarding) - just return success
        return { 
          profileData: null
        };
      }
    },
    onMutate: async (_pictureUrl: string) => {
      const queryKey = getQueryKey();
      
      // Cancel any outgoing refetches
      if (profileId) {
        await queryClient.cancelQueries({ queryKey });
      }

      // Snapshot the previous profile
      const previousProfile = profileId 
        ? queryClient.getQueryData<FreelancerProfileWithEnabled | ClientProfile>(queryKey)
        : null;

      // Optimistically update to remove the image
      if (previousProfile && profileId) {
        const optimisticData = {
          ...previousProfile,
          profile_picture_url: null,
        };
        queryClient.setQueryData(queryKey, optimisticData);
      }

      return { previousProfile };
    },
    onError: (err, _pictureUrl, context) => {
      const queryKey = getQueryKey();
      
      // If the mutation fails, roll back the profile cache
      if (context?.previousProfile && profileId) {
        queryClient.setQueryData(queryKey, context.previousProfile);
      }
      console.error('Profile picture deletion failed:', err);
      toast.error('Failed to remove profile picture. Please try again.');
    },
    onSuccess: (result) => {
      if (profileId) {
        const queryKey = getQueryKey();
        
        // Update cache with the actual server data
        queryClient.setQueryData(queryKey, result.profileData);
        // Also invalidate related lists
        if (profileType === 'freelancer') {
          queryClient.invalidateQueries({ queryKey: ["freelancer-profiles"] });
        }
      }
      
      // Show success toast for both cases (profile exists or onboarding)
      toast.success('Profile picture removed successfully!');
    },
  });

  return {
    uploadProfilePicture: uploadProfilePicture.mutate,
    deleteProfilePicture: deleteProfilePicture.mutate,
    isUploading: uploadProfilePicture.isPending,
    isDeleting: deleteProfilePicture.isPending,
    uploadedImageUrl: uploadProfilePicture.data?.publicUrl || null,
    isUploadSuccess: uploadProfilePicture.isSuccess,
    isDeleteSuccess: deleteProfilePicture.isSuccess,
  };
}